import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatRadioModule } from '@angular/material/radio';
import { MatStepperModule } from '@angular/material/stepper';
import { MatExpansionModule } from '@angular/material/expansion';
import {
  ParkingRightService,
  ParkerService,
  ParkingTypeService,
  CreateParkingRightRequest,
  ParkingRightTypeEnum,
  LicensePlateDto,
  TimeBasedParkingRightDto,
  DurationBasedParkingRightDto,
  DayOfWeek,
  CreateParkingRightRequestTypeDto,
  ParkingSpaceDto,
  ParkingZoneDto,
  AssignedZoneParkersDto,
  LongTermParkersDto,
  ListLongTermParkersRequest,
  CreateLongTermParkersRequest
} from '../../../api/gen';
import { JjamesButtonComponent, Role } from 'shared-lib';
import { TranslatePipe } from '@ngx-translate/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { RoleService } from '../../auth/role-service';

export interface ParkingRightAddDialogData {
  parkingSpace: ParkingSpaceDto;
}

export interface LicensePlateItem {
  id: string;
  fullNumber: string;
  parkerId?: string;
}

@Component({
  selector: 'app-parking-right-add',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatDatepickerModule,
    MatCheckboxModule,
    MatRadioModule,
    MatStepperModule,
    MatExpansionModule,
    JjamesButtonComponent,
    TranslatePipe
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './parking-right-add.component.html',
  styleUrl: './parking-right-add.component.scss'
})
export class ParkingRightAddComponent implements OnInit {
  private fb = inject(FormBuilder);
  private parkingRightService = inject(ParkingRightService);
  private parkerService = inject(ParkerService);
  private parkingTypeService = inject(ParkingTypeService);
  private dialogRef = inject(MatDialogRef<ParkingRightAddComponent>);
  private dialogData = inject<ParkingRightAddDialogData>(MAT_DIALOG_DATA);
  private snackBar = inject(MatSnackBar);
  private roleService = inject(RoleService);

  /** Assigned-Zone rights are Administrator-only — operator admins only get Long-Term/Supplier here. */
  isAdmin = signal(false);

  form!: FormGroup;
  isLoading = signal(false);
  isLoadingTypes = signal(false);
  isAddingPlate = signal(false);
  licensePlates = signal<LicensePlateItem[]>([]);
  assignedZoneTypes = signal<AssignedZoneParkersDto[]>([]);

  // Only Assigned-Zone and Long-Term rights are created here. Pool rights are managed
  // in Organization management, and Easypass passes are issued via the QR-token flow.
  parkingKind = signal<'AssignedZoneParkers' | 'LongTerm'>('AssignedZoneParkers');

  // LongTerm state
  longTermTypes = signal<LongTermParkersDto[]>([]);
  isLoadingLongTermTypes = signal(false);
  isCreatingLongTermType = signal(false);

  get parkingSpace(): ParkingSpaceDto {
    return this.dialogData.parkingSpace;
  }

  get availableZones(): ParkingZoneDto[] {
    return this.parkingSpace?.parkingZones || [];
  }

  rightTypeOptions = [
    { value: ParkingRightTypeEnum.TimeBased, label: 'Time Based' },
    { value: ParkingRightTypeEnum.DurationBased, label: 'Duration Based' }
  ];

  daysOfWeek = [
    { value: DayOfWeek.Monday, label: 'Mon' },
    { value: DayOfWeek.Tuesday, label: 'Tue' },
    { value: DayOfWeek.Wednesday, label: 'Wed' },
    { value: DayOfWeek.Thursday, label: 'Thu' },
    { value: DayOfWeek.Friday, label: 'Fri' },
    { value: DayOfWeek.Saturday, label: 'Sat' },
    { value: DayOfWeek.Sunday, label: 'Sun' }
  ];

  get selectedRightType(): ParkingRightTypeEnum {
    return this.form?.get('rightType')?.value;
  }

  get isTimeBased(): boolean {
    return this.selectedRightType === ParkingRightTypeEnum.TimeBased;
  }

  get isDurationBased(): boolean {
    return this.selectedRightType === ParkingRightTypeEnum.DurationBased;
  }

  // ---- Wizard: kind tiles (step 1) ----
  private readonly allKindTiles = [
    { kind: 'AssignedZoneParkers' as const, icon: 'person_pin', label: 'Assigned Zone', desc: 'Plate(s) bound to specific zones' },
    { kind: 'LongTerm' as const, icon: 'local_shipping', label: 'Long-Term / Supplier', desc: 'Standing right for suppliers (Dauerparker)' },
  ];

  /** Assigned Zone is hidden (not disabled) for anyone below Administrator. */
  get kindTiles() {
    return this.isAdmin() ? this.allKindTiles : this.allKindTiles.filter(t => t.kind !== 'AssignedZoneParkers');
  }

  /** Standalone control for the plate input. */
  get licensePlateControl(): FormControl {
    return this.form.get('licensePlateInput') as FormControl;
  }

  /** Step 2 validity — gates the "Next" button per kind. */
  get isDetailsValid(): boolean {
    const v = this.form.value;
    switch (this.parkingKind()) {
      case 'AssignedZoneParkers':
        return !!v.parkingTypeId && (v.selectedZones?.length ?? 0) > 0;
      case 'LongTerm':
        return this.longTermTypes().length > 0;
      default:
        return false;
    }
  }

  get isPlatesValid(): boolean {
    return this.licensePlates().length > 0;
  }

  get canCreate(): boolean {
    return this.isDetailsValid && this.isPlatesValid && !this.isLoading();
  }

  // ---- Wizard: review summary (step 3) ----
  get selectedKindLabel(): string {
    return this.kindTiles.find(t => t.kind === this.parkingKind())?.label ?? '';
  }

  get reviewTypeName(): string {
    switch (this.parkingKind()) {
      case 'AssignedZoneParkers': {
        const id = this.form.value.parkingTypeId;
        const t = this.assignedZoneTypes().find(x => x.parkingType?.id === id);
        return t?.parkingType?.type?.name || 'Assigned Zone';
      }
      case 'LongTerm':
        return this.form.value.lieferantenname || 'Dauerparker';
      default:
        return '';
    }
  }

  get reviewZoneNames(): string {
    const ids: string[] = this.form.value.selectedZones || [];
    if (!ids.length) return 'All zones';
    return this.availableZones.filter(z => z.id && ids.includes(z.id)).map(z => z.name).join(', ');
  }

  get reviewPlates(): string {
    return this.licensePlates().map(p => p.fullNumber).join(', ');
  }

  async ngOnInit(): Promise<void> {
    this.initForm();
    this.isAdmin.set(await this.roleService.checkAccess(Role.Administrator));

    if (this.isAdmin()) {
      this.loadAssignedZoneTypes();
    } else {
      // Assigned Zone is hidden for this role — land straight on the only kind left.
      this.setParkingKind('LongTerm');
    }
  }

  setParkingKind(kind: 'AssignedZoneParkers' | 'LongTerm'): void {
    this.parkingKind.set(kind);
    if (kind === 'LongTerm' && this.longTermTypes().length === 0) {
      this.loadLongTermTypes();
    }
  }

  private loadLongTermTypes(): void {
    if (!this.parkingSpace?.id || !this.parkingSpace?.createdById) return;
    this.isLoadingLongTermTypes.set(true);
    const request: ListLongTermParkersRequest = {
      availableForParkingSpaces: [this.parkingSpace.id],
      operatorId: this.parkingSpace.createdById,
      includeDeleted: false,
      pageIndex: 0,
      pageSize: 100
    };
    this.parkingTypeService.parkingTypesLongTermParkersFilterPost(request).subscribe({
      next: (result) => {
        this.longTermTypes.set(result.items || []);
        this.isLoadingLongTermTypes.set(false);
      },
      error: () => {
        this.snackBar.open('Failed to load Long-Term types', 'Close', { duration: 3000 });
        this.isLoadingLongTermTypes.set(false);
      }
    });
  }

  createLongTermTypeInline(): void {
    if (!this.parkingSpace?.id || !this.parkingSpace?.createdById) return;
    this.isCreatingLongTermType.set(true);
    const request: CreateLongTermParkersRequest = {
      availableForParkingSpaces: [this.parkingSpace.id]
    };
    this.parkingTypeService.parkingTypesLongTermParkersPost(this.parkingSpace.createdById, request).subscribe({
      next: () => {
        this.snackBar.open('Long-Term type created', 'Close', { duration: 2000 });
        this.isCreatingLongTermType.set(false);
        this.loadLongTermTypes();
      },
      error: () => {
        this.snackBar.open('Failed to create Long-Term type', 'Close', { duration: 3000 });
        this.isCreatingLongTermType.set(false);
      }
    });
  }

  private initForm(): void {
    this.form = this.fb.group({
      parkingTypeId: [null, Validators.required],
      selectedZones: [[]],
      rightType: [ParkingRightTypeEnum.TimeBased, Validators.required],
      licensePlateInput: [''],
      // Time-based fields
      validFrom: [null],
      validTo: [null],
      startTime: [''],
      endTime: [''],
      validDays: [[]],
      isAllDay: [true],
      isRecurring: [false],
      // Duration-based fields
      durationInMinutes: [60],
      isRenewable: [false],
      maxRenewals: [null],
      // LongTerm fields
      name: [''],
      validToEnabled: [false],
      // Generic comment (available for all right kinds)
      comment: [''],
    });
  }

  private loadAssignedZoneTypes(): void {
    if (!this.parkingSpace?.id || !this.parkingSpace?.createdById) return;

    this.isLoadingTypes.set(true);

    this.parkingTypeService.parkingTypesAssignedZoneParkersFilterPost({
      availableForParkingSpaces: [this.parkingSpace.id],
      operatorId: this.parkingSpace.createdById,
      includeDeleted: false
    }).subscribe({
      next: (result) => {
        const items = result.items || [];
        this.assignedZoneTypes.set(items);
        // Pre-select the first type so the operator doesn't have to re-pick it in the dropdown.
        if (items.length && !this.form.get('parkingTypeId')?.value) {
          this.form.get('parkingTypeId')?.setValue(items[0].parkingType?.id ?? null);
        }
        this.isLoadingTypes.set(false);
      },
      error: (error) => {
        console.error('Error loading assigned zone types:', error);
        this.snackBar.open('Failed to load parking types', 'Close', { duration: 3000 });
        this.isLoadingTypes.set(false);
      }
    });
  }

  addLicensePlate(): void {
    const value = this.form.get('licensePlateInput')?.value?.trim().toUpperCase();
    if (!value) {
      this.snackBar.open('Please enter a license plate number', 'Close', { duration: 3000 });
      return;
    }

    this.isAddingPlate.set(true);

    this.parkerService.parkerRegisterLicensePlatePost({ fullNumber: value }).subscribe({
      next: (result: LicensePlateDto) => {
        if (result.id) {
          const parkerId = result.parkersIDs?.at(0);
          const existing = this.licensePlates().find(p => p.id === result.id);
          if (!existing) {
            // AssignedZone and Long-Term rights are single-plate.
            this.licensePlates.set([{ id: result.id!, fullNumber: result.fullNumber || value, parkerId }]);
          }
          this.form.get('licensePlateInput')?.setValue('');
          this.snackBar.open('License plate added', 'Close', { duration: 2000 });
        }
        this.isAddingPlate.set(false);
      },
      error: (error) => {
        console.error('Error registering license plate:', error);
        this.snackBar.open('Failed to register license plate', 'Close', { duration: 3000 });
        this.isAddingPlate.set(false);
      }
    });
  }

  removeLicensePlate(plate: LicensePlateItem): void {
    this.licensePlates.update(plates => plates.filter(p => p.id !== plate.id));
  }

  onSubmit(): void {
    if (this.parkingKind() === 'LongTerm') {
      this.submitLongTerm();
      return;
    }

    if (this.form.invalid) return;

    if (this.licensePlates().length === 0) {
      this.snackBar.open('Please add at least one license plate', 'Close', { duration: 3000 });
      return;
    }

    this.isLoading.set(true);

    const formValue = this.form.value;
    let typeDto: CreateParkingRightRequestTypeDto;

    if (formValue.rightType === ParkingRightTypeEnum.TimeBased) {
      const timeBasedDto: TimeBasedParkingRightDto = {
        type: ParkingRightTypeEnum.TimeBased,
        validFrom: formValue.validFrom ? new Date(formValue.validFrom).toISOString() : null,
        validTo: formValue.validTo ? new Date(formValue.validTo).toISOString() : null,
        startTime: formValue.isAllDay ? null : formValue.startTime || null,
        endTime: formValue.isAllDay ? null : formValue.endTime || null,
        validDays: formValue.validDays?.length > 0 ? formValue.validDays : null,
        isAllDay: formValue.isAllDay,
        isRecurring: formValue.isRecurring,
      };
      typeDto = timeBasedDto;
    } else {
      const durationBasedDto: DurationBasedParkingRightDto = {
        type: ParkingRightTypeEnum.DurationBased,
        validFrom: formValue.validFrom ? new Date(formValue.validFrom).toISOString() : null,
        validTo: formValue.validTo ? new Date(formValue.validTo).toISOString() : null,
        durationInMinutes: formValue.durationInMinutes || 60,
        isRenewable: formValue.isRenewable,
        maxRenewals: formValue.isRenewable ? formValue.maxRenewals : null
      };
      typeDto = durationBasedDto;
    }

    console.log('License Plates:', this.licensePlates());
    const licensePlate = this.licensePlates()[0];
    const request: CreateParkingRightRequest = {
      typeDto,
      parkingTypeId: formValue.parkingTypeId,
      allowedParkingSpaces: [this.parkingSpace.id!],
      allowedLicensePlates: [licensePlate.id],
      allowedParkingZones: formValue.selectedZones,
      purchasedByParker: licensePlate.parkerId,
      purchasedByOrganizationId: null,
      paymentReferenceId: null,
      comment: formValue.comment?.trim() || null
    };

    this.parkingRightService.parkingRightsPost(request).subscribe({
      next: () => {
        this.snackBar.open('Parking right created successfully', 'Close', { duration: 3000 });
        this.isLoading.set(false);
        this.dialogRef.close(true);
      },
      error: (error) => {
        console.error('Error creating parking right:', error);
        this.snackBar.open('Failed to create parking right', 'Close', { duration: 3000 });
        this.isLoading.set(false);
      }
    });
  }

  private submitLongTerm(): void {
    if (this.longTermTypes().length === 0) {
      this.snackBar.open('Please create a Long-Term type for this space first', 'Close', { duration: 3000 });
      return;
    }
    if (this.licensePlates().length === 0) {
      this.snackBar.open('Please add a license plate', 'Close', { duration: 3000 });
      return;
    }

    this.isLoading.set(true);
    const formValue = this.form.value;
    const typeDto: TimeBasedParkingRightDto = {
      type: ParkingRightTypeEnum.TimeBased,
      validFrom: null,
      validTo: formValue.validToEnabled && formValue.validTo
        ? new Date(formValue.validTo).toISOString()
        : null,
      startTime: null,
      endTime: null,
      validDays: null,
      isAllDay: true,
      isRecurring: false
    };

    const licensePlate = this.licensePlates()[0];
    const request: CreateParkingRightRequest = {
      typeDto,
      parkingTypeId: this.longTermTypes()[0].parkingTypeId,
      allowedParkingSpaces: [this.parkingSpace.id!],
      allowedLicensePlates: [licensePlate.id],
      allowedParkingZones: formValue.selectedZones?.length > 0 ? formValue.selectedZones : null,
      purchasedByParker: licensePlate.parkerId || null,
      purchasedByOrganizationId: null,
      paymentReferenceId: null,
      name: formValue.name || null,
      comment: formValue.comment?.trim() || null
    };

    this.parkingRightService.parkingRightsPost(request).subscribe({
      next: () => {
        this.snackBar.open('Long-Term right created successfully', 'Close', { duration: 3000 });
        this.isLoading.set(false);
        this.dialogRef.close(true);
      },
      error: () => {
        this.snackBar.open('Failed to create Dauerparker right', 'Close', { duration: 3000 });
        this.isLoading.set(false);
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(false);
  }
}
